import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useGetFormById, useGetProcessById } from '@/hooks';
import { Loader2 } from 'lucide-react';
import {
  ConditionOperator,
  DesignQuestion,
  extractConditionQuestions,
  getDefaultOperator,
  getInputType,
  getOperatorsForType,
} from '@/lib/form-conditions';

interface ApprovalConditionBuilderProps {
  processId: string;
  condition: {
    formId: string;
    questionId: string;
    operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan' | 'notEquals';
    expectedValue: any;
  };
  onConditionChange: (condition: {
    formId: string;
    questionId: string;
    operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan' | 'notEquals';
    expectedValue: any;
  }) => void;
}

type ExtractedQuestion = DesignQuestion;
type Operator = ConditionOperator;

const ApprovalConditionBuilder: React.FC<ApprovalConditionBuilderProps> = ({
  processId,
  condition,
  onConditionChange,
}) => {
  const { t } = useTranslation();
  const { data: processData, isLoading: isProcessLoading } = useGetProcessById(processId);
  const { data: formData, isLoading: isFormLoading } = useGetFormById(condition.formId || '');
  const [forms, setForms] = useState<Array<{ id: string; name: string }>>([]);
  const [questions, setQuestions] = useState<ExtractedQuestion[]>([]);

  useEffect(() => {
    if (processData?.data?.forms) {
      const processForms = processData.data.forms.map((pf: any) => ({
        id: pf.formId || pf.form?.id,
        name: pf.form?.name || t('certificate.approvalCondition.form'),
      }));
      setForms(processForms);
    }
  }, [processData, t]);

  useEffect(() => {
    if (formData?.design && condition.formId) {
      setQuestions(extractConditionQuestions(formData.design));
    } else {
      setQuestions([]);
    }
  }, [formData, condition.formId]);

  const selectedForm = forms.find((f) => f.id === condition.formId);
  const selectedQuestion = questions.find((q) => q.id === condition.questionId);
  const validOperators = selectedQuestion ? getOperatorsForType(selectedQuestion.type) : [];

  const handleFormChange = (formId: string) => {
    onConditionChange({ ...condition, formId, questionId: '', expectedValue: '' });
  };

  const handleQuestionChange = (questionId: string) => {
    const q = questions.find((q) => q.id === questionId);
    onConditionChange({
      ...condition,
      questionId,
      operator: q ? getDefaultOperator(q.type) : 'equals',
      expectedValue: '',
    });
  };

  const handleOperatorChange = (operator: Operator) => {
    onConditionChange({ ...condition, operator, expectedValue: '' });
  };

  const handleExpectedValueChange = (value: any) => {
    onConditionChange({ ...condition, expectedValue: value });
  };

  const renderExpectedValueInput = () => {
    if (!selectedQuestion) return null;
    const { type, options } = selectedQuestion;

    // Option-based types → show a select
    if ((type === 'Dropdown' || type === 'Checkbox') && options && options.length > 0) {
      return (
        <Select
          value={condition.expectedValue || ''}
          onValueChange={handleExpectedValueChange}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('certificate.approvalCondition.selectExpectedValue')} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Number / date / time → native input with appropriate type
    const inputType = getInputType(type);
    return (
      <Input
        type={inputType}
        value={condition.expectedValue ?? ''}
        onChange={(e) => {
          const val = inputType === 'number'
            ? (parseFloat(e.target.value) || 0)
            : e.target.value;
          handleExpectedValueChange(val);
        }}
        placeholder={t('certificate.approvalCondition.enterExpectedValue')}
      />
    );
  };

  if (isProcessLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('certificate.approvalCondition.title')}</CardTitle>
        <CardDescription>
          {t('certificate.approvalCondition.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {forms.length === 0 ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              {t('certificate.approvalCondition.noFormsFound')}
            </p>
          </div>
        ) : (
          <>
            <div>
              <Label htmlFor="formId">{t('certificate.approvalCondition.form')}</Label>
              <Select value={condition.formId} onValueChange={handleFormChange}>
                <SelectTrigger id="formId">
                  <SelectValue placeholder={t('certificate.approvalCondition.selectForm')} />
                </SelectTrigger>
                <SelectContent>
                  {forms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {condition.formId && (
              <>
                {isFormLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : questions.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      {t('certificate.approvalCondition.noQuestionsFound')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="questionId">{t('certificate.approvalCondition.question')}</Label>
                      <Select value={condition.questionId} onValueChange={handleQuestionChange}>
                        <SelectTrigger id="questionId">
                          <SelectValue placeholder={t('certificate.approvalCondition.selectQuestion')} />
                        </SelectTrigger>
                        <SelectContent>
                          {questions.map((question) => (
                            <SelectItem key={question.id} value={question.id}>
                              {question.label} ({question.type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {condition.questionId && (
                      <>
                        <div>
                          <Label htmlFor="operator">{t('certificate.approvalCondition.operator')}</Label>
                          <Select
                            value={condition.operator}
                            onValueChange={(v) => handleOperatorChange(v as Operator)}
                          >
                            <SelectTrigger id="operator">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {validOperators.map((op) => (
                                <SelectItem key={op} value={op}>
                                  {t(`certificate.approvalCondition.operators.${op}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="expectedValue">
                            {t('certificate.approvalCondition.expectedValue')}
                            {selectedQuestion && (
                              <span className="text-xs text-gray-500 ml-2">
                                ({t('certificate.approvalCondition.questionType')} {selectedQuestion.type})
                              </span>
                            )}
                          </Label>
                          {renderExpectedValueInput()}
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 mb-2">
                              {t('certificate.approvalCondition.conditionPreview')}
                            </div>
                            <div className="font-mono text-xs text-gray-700">
                              {selectedForm?.name || t('certificate.approvalCondition.form')} →{' '}
                              {selectedQuestion?.label || t('certificate.approvalCondition.question')}{' '}
                              {condition.operator}{' '}
                              {condition.expectedValue !== undefined && condition.expectedValue !== ''
                                ? JSON.stringify(condition.expectedValue)
                                : '?'}
                            </div>
                            <div className="text-xs text-gray-500 mt-2">
                              {t('certificate.approvalCondition.certificateWillBeGenerated')}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovalConditionBuilder;
